import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import {
  getSingleVibe,
  cancelVibe as cancelVibeApi,
  acceptRejectVibe,
  handleToggleFavorite,
} from "../api/vibe";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as faSolidHeart } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faRegularHeart } from "@fortawesome/free-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CustomPageHeader from "../components/pages/CustomPageHeader";
import Loader from "../components/Loader";
import ConfirmDialog from "../components/ConfirmDialog";
import secureStorage from "../utils/secureStorage";

const VibeDetail = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const backTo = location.state?.backTo || "/home";
  const activeTab = location.state?.activeTab || "sent";
  const navigate = useNavigate();
  const params = useParams();
  const [vibe, setVibe] = useState(location.state?.vibe || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(false);

  // Cancel Vibe API - WITHOUT RE-FETCH
  const cancelVibe = async () => {
    setCancelling(true);
    try {
      await cancelVibeApi(vibe.id);
      // Update state directly instead of re-fetching
      setVibe(prev => ({ ...prev, vibe_status: 3 }));
      setShowCancelModal(false);
      toast.success("Vibe cancelled successfully!");
    } catch (err) {
      toast.error("Failed to cancel vibe.");
    } finally {
      setCancelling(false);
    }
  };

  const handleAcceptReject = async (action) => {
    // action: 1 for accept, 2 for reject
    try {
      await acceptRejectVibe(vibe.id, action);
      // Update state directly instead of re-fetching
      const storedUser = secureStorage.getJSON("user");
      const currentUserId = storedUser?.id || null;
      
      setVibe(prev => ({
        ...prev,
        members: prev.members.map(member => {
          if (Number(member.user?.id || member.id) === Number(currentUserId)) {
            return { ...member, status: action };
          }
          return member;
        })
      }));
      
      toast.success(action === 1 ? "Vibe accepted!" : "Vibe rejected!");
    } catch (err) {
      toast.error("Failed to process vibe action.");
    }
  };

  // Heart icon handler - WITHOUT RE-FETCH
  const handleFavoriteClick = async () => {
    if (togglingFavorite) return;
    setTogglingFavorite(true);
    
    // Update state directly instead of re-fetching
    const newFavoriteState = !vibe.is_favorite;
    setVibe(prev => ({ ...prev, is_favorite: newFavoriteState }));
    
    try {
      await handleToggleFavorite(vibe.id);
    } catch (err) {
      // Revert on error
      setVibe(prev => ({ ...prev, is_favorite: !newFavoriteState }));
      // Error handling is done by centralized error handler
    } finally {
      setTogglingFavorite(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (params.id && !vibe) {
      setLoading(true);
      getSingleVibe(params.id)
        .then((res) => {
          if (!isMounted) return;

          let data = res && res.data ? res.data : res;
          if (data && data.data) data = data.data;
          setVibe(data);
          setError(null);
        })
        .catch((err) => {
          if (!isMounted) return;
          // Only show error if not 429 (Too Many Requests)
          if (err?.response?.status === 429) {
            setError(null); // Don't show error for rate limit
          } else {
            setError("Could not load vibe details.");
            // Error toast is handled by centralized error handler
          }
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [params.id, vibe]);

  // Helper: convert backend UTC date (YYYY-MM-DD) + time (HH:MM or HH:MM AM/PM) into local date/time strings and Date
  const convertUTCDateTimeToLocal = (dateStr, timeStr) => {
    if (!dateStr)
      return { localDateObj: null, localDateStr: null, localTimeStr: null };
    try {
      // Build an ISO UTC string and let Date parse it as UTC
      const iso = `${dateStr}T${timeStr}:00Z`;
      const utcDate = new Date(iso);
      if (isNaN(utcDate.getTime()))
        return { localDateObj: null, localDateStr: null, localTimeStr: null };

      const localDateObj = new Date(utcDate.getTime());

      // Format as d/m/y (e.g., 31/12/2025)
      const d = localDateObj.getDate().toString().padStart(2, "0");
      const m = (localDateObj.getMonth() + 1).toString().padStart(2, "0");
      const y = localDateObj.getFullYear();
      const localDateStr = `${m}/${d}/${y}`;

      const hour = localDateObj.getHours();
      const minute = localDateObj.getMinutes().toString().padStart(2, "0");
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      const localTimeStr = `${hour12
        .toString()
        .padStart(2, "0")}:${minute} ${ampm}`;

      return { localDateObj, localDateStr, localTimeStr };
    } catch (err) {
      return { localDateObj: null, localDateStr: null, localTimeStr: null };
    }
  };

  if (loading) {
    return (
      <Layout hideSidebar hideFooter>
        <div className="p-4">
          <Loader />
        </div>
      </Layout>
    );
  }
  if (error) {
    return (
      <Layout hideSidebar hideFooter>
        <div className="p-4 text-red-500">{error}</div>
      </Layout>
    );
  }
  if (!vibe) {
    const backTo = location.state?.backTo || "/home";
    return (
      <Layout hideSidebar hideFooter>
        <div className="p-4">
          <div className="text-red-500 mb-4">
            No vibe data found. Please go back and select a vibe.
          </div>
          <button
            className="px-4 py-2 bg-[#6d2af6] text-white rounded"
            onClick={() => navigate(backTo)}
          >
            Go Back
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <CustomPageHeader
        pageContainerClass="px-1 sm:px-2 mt-4 sm:mt-6 md:mt-8 mb-2 sm:mb-4 md:mb-6 pt-4 sm:pt-6 md:pt-8 pb-2 sm:pb-4 md:pb-6 max-w-full sm:max-w-4xl md:max-w-5xl lg:max-w-6xl"
        title={t("vibe_detail", "Vibe Card")}
        onBack={() => navigate(backTo)}
      >
        <div className="text-gray-800 max-w-md mx-auto">
          {/* Title + Heart aligned with card */}
          {vibe.vibe_category?.name && (() => {
            const storedUser = secureStorage.getJSON("user");
            const currentUserId = storedUser?.id || null;
            const showFavoriteIcon = currentUserId && Number(currentUserId) !== Number(vibe.user_id);
            
            return (
              <div className={`flex items-center mb-2 sm:mb-8 md:mb-10 -mt-10 md:mt-0 lg:mt-0 px-6 ${showFavoriteIcon ? 'justify-between' : 'justify-center'}`}>
                {/* Title */}
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">
                  {vibe.vibe_category?.name || vibe.category?.name}
                </h1>

                {/* Heart Icon - Only show for vibes user doesn't own */}
                {showFavoriteIcon && (
                  <button
                    className="p-1 focus:outline-none"
                    onClick={handleFavoriteClick}
                    disabled={togglingFavorite}
                  >
                    <FontAwesomeIcon
                      icon={vibe.is_favorite ? faSolidHeart : faRegularHeart}
                      style={{
                        color: "#6d2af6",
                        fontSize: 28,
                        border: "none",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                      }}
                    />
                  </button>
                )}
              </div>
            );
          })()}

          {/* Main Card Container - iPad Optimized with better width usage */}
      
          <div className="overflow-y-auto overflow-x-hidden max-h-[calc(100vh-80px)] pb-6">
              <div className="bg-white dark:bg-[#252739] rounded-3xl shadow-xl py-5 sm:py-6 md:py-8 px-4 sm:px-6 md:px-8 w-full max-w-md mx-auto vibe-detail-card" style={{ width: '100%', maxWidth: '28rem' }}>
              {/* Card Content Grid - Always show 2 columns on wide enough screens */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
                {/* Left Column */}
                <div className="space-y-5">
                  {/* The Vibe */}
                  <div>
                    <div className="text-gray-500 font-medium text-base mb-2">
                      {t("the_vibe")}
                    </div>
                    <div className="font-bold text-gray-800 text-md">
                      {vibe.vibe_category?.name ||
                        vibe.category?.name ||
                        "No Category"}
                    </div>
                  </div>

                  {/* The Links */}
                  <div>
                    <div className="text-gray-500 font-medium text-base mb-2">
                      {t("the_links")}
                    </div>
                    <div className="font-bold text-gray-800 text-md leading-relaxed">
                      {Array.isArray(vibe.vibe_links) &&
                        vibe.vibe_links.length > 0 &&
                        vibe.vibe_links[0]?.name
                        ? vibe.vibe_links.map((link) => link.name).join(", ")
                        : "Individual Members"}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-5 min-w-0 break-words">
                  {/* The Place */}
                  <div>
                    <div className="text-gray-500 font-medium text-base mb-2">
                      {t("the_place")}
                    </div>
                    <div className="font-bold text-gray-800 text-md leading-relaxed">
                      {vibe.venue ||
                        (vibe.address?.city ? vibe.address.city : "-")}
                    </div>
                  </div>

                  {/* Date + Time */}
                  <div>
                    <div className="text-gray-500 font-medium text-base mb-2">
                      {t("date_time", "Date + Time")}
                    </div>
                    <div className="font-bold text-gray-800 text-md">
                      {(() => {
                        const { localDateStr, localTimeStr } =
                          convertUTCDateTimeToLocal(vibe.date, vibe.time);
                        return (
                          <div>
                            <div>{localDateStr || ""}</div>
                            {vibe.time && <div>{localTimeStr || vibe.time}</div>}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

               {/* Input Fields with Card Design */}
               <div className="space-y-5">
                 {/* Attire */}
                 <div className="flex items-center justify-center">
                   <span className="text-gray-600 dark:text-dark-text-secondary font-medium text-base mr-4 w-32">
                     {t("attire")}
                   </span>
                   <div className="bg-blue-100 rounded-2xl px-5 py-3 min-w-[140px] text-center">
                     <span className="text-center font-medium text-sm" style={{ color: '#000000', WebkitTextFillColor: '#000000' }}>
                       {vibe.attire || "Casual"}
                     </span>
                   </div>
                 </div>

                 {/* Chip In */}
                 <div className="flex items-center justify-center">
                   <span className="text-gray-600 dark:text-dark-text-secondary font-medium text-base mr-4 w-32">
                     {t("chip_in", "Chip In")}
                   </span>
                   <div className="bg-green-100 rounded-2xl px-5 py-3 min-w-[140px] text-center">
                     <span className="text-center font-medium text-sm" style={{ color: '#000000', WebkitTextFillColor: '#000000' }}>
                       {vibe.chip_in !== undefined && vibe.chip_in !== null
                         ? vibe.chip_in
                         : "0.00"}
                     </span>
                   </div>
                 </div>

                 {/* Venue Incentive */}
                 <div className="flex items-center justify-center">
                   <span className="text-gray-600 dark:text-dark-text-secondary font-medium text-base mr-4 w-32">
                     {t("venue_incentive", "Venue Incentive")}
                   </span>
                   <div className="bg-purple-100 rounded-2xl px-5 py-3 min-w-[140px] text-center">
                     <span className="text-center font-medium text-sm" style={{ color: '#000000', WebkitTextFillColor: '#000000' }}>
                       {vibe.venue_incentive !== undefined &&
                       vibe.venue_incentive !== null
                         ? vibe.venue_incentive
                         : "FreeApp"}
                     </span>
                   </div>
                 </div>
               </div>
            </div>
          </div>

          {/* Action Buttons - Reduced spacing for tablets */}
          <div className="mt-3 sm:mt-4 md:mt-6 flex flex-row items-center justify-center gap-3 w-full max-w-md mx-auto flex-wrap px-3 sm:px-4 md:px-6 lg:px-8">
              {(() => {
                // Expired logic
                let isExpired = false;
                if (vibe.date && vibe.time) {
                  const { localDateObj } = convertUTCDateTimeToLocal(
                    vibe.date,
                    vibe.time
                  );
                  const now = new Date();
                  if (localDateObj) isExpired = now > localDateObj;
                }

                if (isExpired) {
                  return (
                    <div className="flex gap-3 w-full max-w-md">
                      <button
                        className="flex-1 px-6 py-2 rounded bg-red-500 text-white font-semibold cursor-not-allowed whitespace-nowrap"
                        disabled
                      >
                        {t("vibe_expired")}
                      </button>
                    </div>
                  );
                }

                // Cancelled case
                const storedUser = secureStorage.getJSON("user");
                const currentUserId = storedUser?.id || null;
                const isCancelled =
                  vibe.vibe_status === 3 ||
                  vibe.vibe_status === 4 ||
                  vibe.vibe_status === "cancelled";

                if (isCancelled) {
                  return (
                    <div className="flex gap-3 w-full max-w-md">
                      <button
                        className="flex-1 px-6 py-2 rounded bg-red-500 text-white font-semibold cursor-not-allowed whitespace-nowrap"
                        disabled
                      >
                        {t("vibe_cancelled")}
                      </button>
                    </div>
                  );
                }

                // Owner of vibe
                if (
                  currentUserId &&
                  Number(currentUserId) === Number(vibe.user_id)
                ) {
                  return (
                    <div className="flex gap-3 w-full max-w-md">
                      <button
                        className="flex-1 px-6 py-2 text-[12px] lg:text-lg rounded bg-red-500 text-white font-semibold hover:bg-red-600 transition whitespace-nowrap"
                        onClick={() => setShowCancelModal(true)}
                        disabled={cancelling}
                      >
                        {t("cancel_vibe")}
                      </button>
                      <button
                        className="flex-1 px-6 py-2 text-[12px] lg:text-lg rounded bg-[#6d29f6] text-white font-semibold hover:bg-purple-800 transition whitespace-nowrap"
                        onClick={() =>
                          navigate("/vibe-members", {
                            state: { members: vibe.members, backTo },
                          })
                        }
                      >
                        {t("members")}
                      </button>
                    </div>
                  );
                }

                // Member cases
                let memberObj = null;
                if (
                  Array.isArray(vibe.members) &&
                  vibe.members.length > 0 &&
                  currentUserId
                ) {
                  memberObj = vibe.members.find(
                    (m) => Number(m.user?.id || m.id) === Number(currentUserId)
                  );
                }

                if (memberObj) {
                  if (memberObj.status === 0) {
                    return (
                      <div className="flex gap-3 w-full max-w-md">
                        <button
                          className="flex-1 px-6 py-2 rounded bg-red-500 text-white font-semibold hover:bg-red-600 transition whitespace-nowrap"
                          onClick={() => handleAcceptReject(2)}
                        >
                          {t("reject_vibe")}
                        </button>
                        <button
                          className="flex-1 px-6 py-2 rounded bg-[#6d29f6] text-white font-semibold hover:bg-purple-800 transition whitespace-nowrap"
                          onClick={() => handleAcceptReject(1)}
                        >
                          {t("accept_vibe")}
                        </button>
                      </div>
                    );
                  } else if (memberObj.status === 1) {
                    return (
                      <div className="flex gap-3 w-full max-w-md">
                        <button
                          className="flex-1 px-6 py-2 rounded bg-[#6d29f6] text-white font-semibold cursor-not-allowed whitespace-nowrap"
                          disabled
                        >
                          {t("accepted")}
                        </button>
                      </div>
                    );
                  } else if (memberObj.status === 2) {
                    return (
                      <div className="flex gap-3 w-full max-w-md">
                        <button
                          className="flex-1 px-6 py-2 rounded bg-red-500 text-white font-semibold cursor-not-allowed whitespace-nowrap"
                          disabled
                        >
                          {t("rejected")}
                        </button>
                      </div>
                    );
                  }
                }

                return null;
              })()}

              {/* Cancel Confirmation Modal */}
              <ConfirmDialog
                open={showCancelModal}
                title={t("app_name")}
                message={t("cancel_vibe_confirm")}
                onConfirm={cancelVibe}
                onCancel={() => {
                  setShowCancelModal(false);
                }}
                yesLabel={t("Sidebar.logout_yes")}
                noLabel={t("Sidebar.logout_no")}
              />
          </div>
        </div>
      </CustomPageHeader>
    </>
  );
};

export default VibeDetail;